import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { RelatedSpace, Space } from '@app/core/model/entities/asset/space';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { AbstractFieldBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GridStateService } from '@app/shared/services/grid-state.service';
import { GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, EMPTY, exhaustMap, merge, Observable, pipe, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'space-path-field-builder',
  templateUrl: './space-path-field-builder.component.html',
  styleUrls: ['./space-path-field-builder.component.scss'],
  standalone: false
})
export class SpacePathFieldBuilderComponent extends AbstractFieldBuilder<FormControl<RelatedSpace | Space>> implements OnInit, AfterViewInit {
  private gridOptionsService = inject(GridOptionsService);
  private spacesService = inject(SpacesService);
  private columnBuilder = inject(ColumnBuilder);
  private gridStateService = inject(GridStateService);
  private deviceDetectorService = inject(DeviceDetectorService);

  public noAsset = true;
  public selectedSpace: (RelatedSpace | Space);
  public initialParentSpace: Space;
  public widthAuto = true;
  public gridApi: GridApi<Space>;
  public gridOptions: GridOptions<Space>;
  protected invalidRows: string[] = [];
  protected spaces = new Subject<Space[]>();
  protected assetChanged$ = new BehaviorSubject<string>(null);
  protected assetTypeAllowsSpaceModule = false;

  /**
   * Key is space's id and value is space's display name
   * If there is no space, key is null and string is this.appConfig.EMPTY_FIELD_VALUE
   */
  public spacesMap = new Map<string, string>();

  @ViewChild('gridContainer') public gridContainer: ElementRef;

  protected cancel$ = new Subject<void>();

  constructor() {
    super();
    // Add additional options to the gridOption
    const extraSpaceGridOptions = <GridOptions>{
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
      },
      suppressContextMenu: false,
      multiSortKey: this.appConfig.GRID_MULTI_SORT_KEY,
      domLayout: 'autoHeight',
      getRowId: (params) => params.data.id,
      animateRows: true,
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center">' + this.translate.instant('LABEL.LOADING') + '</span>',
      localeText: this.columnBuilder.localeTextCommon(),
      rowClassRules: {
        'invalid-row': (params): boolean => this.invalidRows.includes(params.data.id)
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      getMainMenuItems: (params) => params.defaultItems,
      getRowHeight: () => this.gridOptionsService.staticGridOptions.rowHeight,
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this),
      onGridSizeChanged: () => this.gridApi.sizeColumnsToFit()
    };
    this.gridOptions = {
      ...this.gridOptionsService.staticGridOptions,
      ...extraSpaceGridOptions
    };
  }

  /**
   * Initializes the form group and corresponding form control. Once the formStateService is loaded,
   * fetches the space given a spaceId which will be used as the form control's initial value.
   * Subscribes to changes in the form's asset, which reevaluates the allowsSpaceModule flag, which in turn
   * determines if the field should be displayed.
   * Subscribes to the change in state to fetch all pages of data to fill the datagrid.
   */
  public ngOnInit() {
    // Initialise the form so it exists before the space is actually loaded
    this.initialParentSpace = null;
    this.form = new FormGroup<{ field: FormControl<(RelatedSpace | Space)> }>({
      field: new FormControl<RelatedSpace | Space>(
        this.initialParentSpace
      )
    });

    // Reusable pipeline
    const reloadNewPage = pipe(
      filter(() => this.currentMode === FieldStateMode.EDIT),
      tap(() => this.gridApi?.showLoadingOverlay()),
      // Ignore all new inputs until the api call completes
      exhaustMap(() => {
        return this.loadAllSpaces(this.assetChanged$.value);
      }),
      // Emit the accumulation of existing data and the new page
      tap(entitiesNewPage => this.spaces.next(entitiesNewPage))
    );

    merge(
      this.assetChanged$.pipe(
        reloadNewPage
      ),
      this.getFieldGroupState().pipe(
        filter(newMode => newMode === FieldStateMode.EDIT),
        debounceTime(500),
        reloadNewPage
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    // FIXME TTT-4797 SSRM on space tree data
    this.formStateService.loaded$
      .pipe(
        filter(() => Array.isArray(this.fieldInitValue) && this.fieldInitValue.length > 0),
        // Load space
        switchMap(() => {
          return this.spacesService.loadSpace(this.fieldInitValue?.last())
            .pipe(
              tap(parentSpace => {
                this.initialParentSpace = parentSpace;
                this.form.controls.field.setValue(this.initialParentSpace.toRelatedSpace());
                this.form.controls.field.setValidators(this.computeValidators());
                this.form.controls.field.setAsyncValidators(this.computeAsyncValidators());

                this.setSelectedEntities();
              })
            );
        }),
        switchMap(() => {
          if (this.getFormChanges(['asset'])) {
            // If the field group contains an Asset field whose value can change, load Spaces whenever a new Asset is selected
            return this.getFormChanges(['asset'])
              .pipe(
                filter(changes => !!changes.value),
                // Load spaces if the asset module allows it
                tap((changes) => {
                  const asset: RelatedAsset = changes.value;
                  this.assetTypeAllowsSpaceModule = asset?.typeModules?.includes(EntityTypeEnum.SPACE);

                  this.noAsset = !asset?.id;
                  this.assetChanged$.next(asset?.id);
                })
              );
          } else {
            // Form does not contain an Asset field
            this.noAsset = false;
            this.assetChanged$.next(this.initialParentSpace.assetId);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();

    // this.viewToggled and this.editToggled are initialised but never re-written
    // Use their value to initialise a local variable which can change over time
    this.assetTypeAllowsSpaceModule = this.viewToggled && this.editToggled;
  }

  /**
   * Subscribes to the form field's value changes to update the space path in the form state service.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();
    this.form.controls.field.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((newParent) => {
        this.setFieldValue([...newParent[this.fieldConfig.fieldCode], newParent.id]);
      });
  }

  /**
   * Load all spaces related to an asset by building corresponding select params
   * @param assetId The asset's ID
   * @returns The list of spaces
   */
  private loadAllSpaces(assetId: string): Observable<Space[]> {
    const selectParams: SelectParams = {
      locale: getLocale(),
      filters: [eq('assetId', assetId)]
    };

    return this.spacesService.loadAllSpaces(selectParams);
  }

  /**
   * This method will set selectable spaces in the grid.
   * @param spaces A list of Space entities
   */
  protected setGridData(spaces: Space[]): void {
    this.gridApi.setGridOption('rowData', spaces.filter(space => !([...space.parentPath, space.id].includes(this.entity.id))));
  }

  /**
   * This method will set current selected space by fetching the direct parent (last element of the list).
   */
  protected setSelectedEntities(): void {
    this.selectedSpace = this.initialParentSpace;
    const fieldValue = this.selectedSpace ? [this.selectedSpace.id] : [];
    this.setFieldValue(fieldValue.map(space => space.id));
    this.setFieldInitialValue(fieldValue.map(space => space.id));
    this.getNextState();
    this.spacesMap.set(this.selectedSpace.id, this.selectedSpace.toString());
    if (this.spacesMap.size === 0) {
      this.spacesMap.set(null, this.appConfig.EMPTY_FIELD_VALUE);
    }
  }

  /**
   * Event thrown by the grid once it is loaded
   * @param params
   */
  public onGridReady(params: GridReadyEvent): void {
    this.invalidRows = [];
    this.gridApi = params.api;
    this.gridApi.applyColumnState({state: [{colId: 'ag-Grid-AutoColumn', sort: 'asc'}]});
    if (this.deviceDetectorService.isMobile()) {
      this.gridApi.applyColumnState({
        state: [
          {colId: 'ag-Grid-AutoColumn'},
          {colId: 'identifier'}
        ]
      });
    }
    this.spaces.pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe(spaces => {
        this.setGridData(spaces);
        this.gridApi.sizeColumnsToFit();
        const rowNode = this.gridApi.getRowNode(this.selectedSpace.id);
        if (rowNode?.data?.parentPath) {
          rowNode.data.parentPath.forEach((parentId: string) => {
            this.gridApi.getRowNode(parentId).setExpanded(true);
          });
        }
        rowNode?.setSelected(true);
        spaces.length ? this.gridApi.hideOverlay() : this.gridApi.showNoRowsOverlay();
      });

    // Focus and scroll to field
    this.gridContainer.nativeElement.focus();
    this.gridContainer.nativeElement.scrollIntoView({behavior: 'instant', block: 'center'});
  }

  /**
   * Handles the save operation triggered by the user click event.
   */
  public onClickSave(): void {
    if (this.gridApi.getSelectedRows().length > 0) {
      super.onClickSave();
    } else {
      this.cancel();
    }
  }

  /**
   * Event thrown by the grid when a row is selected or deselected.
   * @param event RowSelectedEvent
   */
  public onRowSelected(event: RowSelectedEvent): void {
    this.selectedSpace = this.gridApi.getSelectedRows()?.[0]?.toRelatedSpace();
    this.form.controls.field.setValue(this.selectedSpace);
    this.form.controls.field.markAsDirty();

    const nodesToRefresh = [event.node];

    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }

    // Refresh rows
    this.gridApi.redrawRows({rowNodes: nodesToRefresh});
  }

  /**
   * Restore field's value to original Spaces.
   */
  public cancel(): void {
    super.cancel();
    this.selectedSpace = this.initialParentSpace;
    this.cancel$.next();
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return true if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => space.parentPath.includes(selectedRow.id));
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return true is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => selectedRow.parentPath.includes(space.id));
  }

  /**
   * Navigate to the Space sheet of the related Asset
   */
  public async navigateToAssetSpaceSheet(): Promise<void> {
    if (this.assetTypeAllowsSpaceModule) return;

    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.entity['assetId']
    );
    await this.spacesService.navigateToAssetSheet(this.entity['assetId']);
  }

  /**
   * Open the Space sheet of the related Asset and specify which row to select in datagrid
   * @param spaceId
   */
  public onSpaceFieldClick(spaceId: string): void {
    if (spaceId == null) return;

    this.navigateToAssetSpaceSheet().then(() => {
      this.gridStateService.setRowToSelect(spaceId);
    });
  }
}
